import type { ReactNode } from 'react'
import { HelpCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@atrvd/saas-ui'

interface HeaderWithHintProps {
  /** The column's name; also names the hint icon ("About <label>"). */
  label: string
  /** The explanation shown on hover/focus. */
  hint: string
  /** What to show as the label instead of the plain text, e.g. a `SortHeader`. */
  children?: ReactNode
}

/**
 * A column header that carries an explanatory hint icon.
 *
 * This is what `DataTableColumn.header` accepting a ReactNode is for. The
 * tooltip content goes through `TooltipPortal` because the header sits inside
 * the table's horizontal scroll wrapper (`overflow-x-auto`), which would
 * otherwise clip the bubble instead of letting it float over the page.
 */
export function HeaderWithHint({ label, hint, children }: HeaderWithHintProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {children ?? label}
      <Tooltip>
        <TooltipTrigger asChild>
          {/*
            A <span>, not a <button>: a sortable column renders its header
            INSIDE the sort button, and a nested interactive control is
            unreachable by keyboard. `tabIndex` keeps the hint itself focusable
            for the columns that are not sortable.
          */}
          <span tabIndex={0} className="inline-flex cursor-help text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" aria-label={`About ${label}`} />
          </span>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="max-w-xs">{hint}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </span>
  )
}
